import { renderToString } from 'react-dom/server';
import { getServerState } from 'react-instantsearch';

import { DynamicComponents, Error, ListOfFlats } from '@/templates';

import { FLAT_FILTER_SLUGS } from '@/components/algolia/flats-routing';
import { flatSortIndexForSlug } from '@/components/algolia/flats-sort';
import { convertToURL } from '@/components/algolia/utils';
import { canonicalZoneHierarchical } from '@/components/algolia/zone-canonical';
import { getPage } from '@/graphql/queries';
import { dynamicComponentsTransformer } from '@/graphql/transformers';
import { routes } from '@/lib';
import {
  generateHierarchical,
  getPathFromUrl,
  listingChromeFor,
} from '@/utils';

import type { TypeTheme } from '@/types';
import type { ListingChromeProps } from '@/utils';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type {
  InstantSearchProps,
  InstantSearchServerState,
} from 'react-instantsearch';

type FlatCategoryProps = {
  page: {
    title: string;
    metaDescription: string;
    metaKeywords: null;
    metaRobots: string;
    path: string;
    theme: TypeTheme;
    langcode: {
      id: 'es';
    };
    // TODO: tipar
    attributes: any;
    menu: any[];
    preFooter: any[];
  };
  serverState: InstantSearchServerState;
  errorCode?: number;
  defaultArea: string;
  initialUiState: InstantSearchProps['initialUiState'];
  statusCode?: number;
  chrome: ListingChromeProps;
  serverUrl: string;
};

const FlatCategory = ({
  serverState,
  serverUrl,
  defaultArea,
  errorCode,
  initialUiState,
  chrome,
  page: {
    attributes: { Content: dynamicContent },
  },
}: InferGetServerSidePropsType<typeof getServerSideProps>) =>
  errorCode === 404 ? (
    <Error statusCode={errorCode} />
  ) : (
    <>
      <ListOfFlats
        defaultArea={defaultArea}
        serverState={serverState}
        serverUrl={serverUrl}
        type="VENTA"
        typeText="en venta"
        initialUiState={initialUiState}
        {...chrome}
      />

      {dynamicContent.map(({ __typename, ...props }: any, index: number) => (
        <DynamicComponents
          type={__typename}
          {...props}
          key={`${__typename}-${index}`}
        />
      ))}
    </>
  );

export const getServerSideProps: GetServerSideProps<
  FlatCategoryProps
> = async ({ req, resolvedUrl, res, query }) => {
  const {
    pages,
    menus,
    preFooter,
    hasCustomFooter,
    footerStyle,
    stickyMenu,
    listingChrome,
  } = await getPage(routes.flats.sale.index);
  const chrome = listingChromeFor(listingChrome, 'VENTA');
  const attributes = pages?.[0] || {};
  attributes.Content = attributes?.Content
    ? dynamicComponentsTransformer(attributes.Content)
    : [];

  const categories = getPathFromUrl(resolvedUrl)
    .split(routes.flats.sale.path)
    .join('')
    .split('/')
    .filter(Boolean);
  const {
    hierarchical: rawHierarchical,
    name: defaultArea,
    path,
  } = generateHierarchical(categories);
  // Valor canónico del facet (mayúsculas/acentos reales): sin él el
  // hierarchicalMenu no marca la zona como refinada ni despliega sus hijos.
  const hierarchical = await canonicalZoneHierarchical(rawHierarchical);
  const resultsPath = convertToURL(routes.flats.sale.zone(path));
  const title = `Venta pisos - Pisos en venta en ${defaultArea}`;

  const proto = ((req.headers['x-forwarded-proto'] as string) || 'http').split(
    ',',
  )[0];
  const serverUrl = `${proto}://${req.headers.host}${resolvedUrl}`;

  const initialUiState = {
    [process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? '']: {
      hierarchicalMenu: {
        'hierarchicalZonas.lvl0': [hierarchical],
      },
    },
  };
  const serverState = await getServerState(
    <ListOfFlats
      initialUiState={initialUiState}
      serverUrl={serverUrl}
      defaultArea={defaultArea}
      type="VENTA"
      typeText="en venta"
      {...chrome}
    />,
    {
      renderToString,
    },
  );

  const results =
    serverState.initialResults[
      // Con `?orden=` activo, InstantSearch busca contra la réplica, así que
      // los resultados SSR vienen bajo la clave de la réplica y no bajo la del
      // índice principal. Buscarlos siempre en el principal haría 404 toda URL
      // ordenada.
      flatSortIndexForSlug(
        process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? '',
        typeof query?.orden === 'string' ? query.orden : null,
      )
    ]?.results?.[0];
  // Sólo 404 por zona inválida. Con el routing de filtros, el estado SSR ya
  // refleja los query params, así que un filtro muy restrictivo puede dar
  // nbHits===0 en una zona válida — eso es un listado vacío, no un 404.
  const hasFilterParams = FLAT_FILTER_SLUGS.some(
    (slug) => query?.[slug] != null,
  );
  const notFound = !results || (results.nbHits === 0 && !hasFilterParams);

  if (notFound) res.statusCode = 404;

  return {
    props: {
      page: {
        title,
        metaDescription: title,
        metaKeywords: null,
        metaRobots: '',
        path: resultsPath,
        theme: 'light' as TypeTheme,
        langcode: {
          id: 'es',
        },
        attributes,
        menu: menus || [],
        preFooter: preFooter || null,
        hasCustomFooter,
        footerStyle,
        stickyMenu,
      },
      serverState: JSON.parse(JSON.stringify(serverState)),
      defaultArea,
      chrome,
      serverUrl,
      ...(notFound && { errorCode: 404 }),
      initialUiState,
    },
  };
};

export default FlatCategory;
